import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Send, TrendingUp, Calculator, Clock, CheckCircle2 } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function ROICalculator() {
  const { lang } = useLang();

  // Format presets based on site data
  const formatPresets = {
    standard: { investment: 150000, profit: 6000 },
    recommended: { investment: 250000, profit: 8000 },
    expanded: { investment: 300000, profit: 10000 },
  };

  const [selectedFormat, setSelectedFormat] = useState('recommended');
  const [investment, setInvestment] = useState(formatPresets.recommended.investment);
  const [monthlyProfit, setMonthlyProfit] = useState(formatPresets.recommended.profit);
  const [animatedPayback, setAnimatedPayback] = useState(0);

  const content = lang === 'ru' ? {
    title: 'Калькулятор окупаемости',
    subtitle: 'Оцените возможную модель запуска на основе базовых показателей.',
    
    format_label: 'Формат запуска',
    formats: [
      { value: 'standard', label: 'Стандартный' },
      { value: 'recommended', label: 'Рекомендуемый' },
      { value: 'expanded', label: 'Расширенный' },
    ],
    
    investment_label: 'Общий объём инвестиций',
    profit_label: 'Ожидаемая ежемесячная прибыль',
    
    result_title: 'Расчёт окупаемости',
    payback_label: 'Окупаемость',
    payback_unit: 'месяцев',
    profit_per_month: 'Прибыль в месяц',
    
    disclaimer: 'Расчёт ориентировочный и зависит от формата, города и модели запуска.',
    
    cta_primary: 'Обсудить расчёт',
    cta_secondary: 'Написать в Telegram',
  } : {
    title: 'Calculadora de retorno de inversión',
    subtitle: 'Evalúe el posible modelo de lanzamiento basado en indicadores básicos.',
    
    format_label: 'Formato de lanzamiento',
    formats: [
      { value: 'standard', label: 'Estándar' },
      { value: 'recommended', label: 'Recomendado' },
      { value: 'expanded', label: 'Ampliado' },
    ],
    
    investment_label: 'Volumen total de inversión',
    profit_label: 'Beneficio mensual esperado',
    
    result_title: 'Cálculo de retorno',
    payback_label: 'Retorno',
    payback_unit: 'meses',
    profit_per_month: 'Beneficio por mes',
    
    disclaimer: 'El cálculo es orientativo y depende del formato, ciudad y modelo de lanzamiento.',
    
    cta_primary: 'Discutir el cálculo',
    cta_secondary: 'Escribir por Telegram',
  };

  // Calculate payback period
  const paybackMonths = Math.round(investment / monthlyProfit);

  // Animate payback number change
  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const stepValue = paybackMonths / steps;
    let current = 0;

    const interval = setInterval(() => {
      current += stepValue;
      if (current >= paybackMonths) {
        setAnimatedPayback(paybackMonths);
        clearInterval(interval);
      } else {
        setAnimatedPayback(Math.round(current));
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, [paybackMonths]);

  const handleFormatChange = (format) => {
    setSelectedFormat(format);
    setInvestment(formatPresets[format].investment);
    setMonthlyProfit(formatPresets[format].profit);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  return (
    <section className="relative py-20 md:py-28 px-6 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgba(212,175,55,0.1) 0%, transparent 60%)',
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calculator size={32} className="text-[#D4AF37]" />
            <h2 className="text-3xl md:text-5xl font-black font-heading gold-text leading-tight">
              {content.title}
            </h2>
          </div>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="glass-premium rounded-3xl p-8"
          >
            {/* Format Selection */}
            <div className="mb-8">
              <label className="block text-[#D4AF37] text-sm font-bold uppercase tracking-wider mb-4">
                {content.format_label}
              </label>
              <div className="grid grid-cols-1 gap-3">
                {content.formats.map((format) => (
                  <motion.button
                    key={format.value}
                    onClick={() => handleFormatChange(format.value)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      relative p-4 rounded-xl border-2 transition-all duration-300 text-left
                      ${selectedFormat === format.value
                        ? 'border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-transparent'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-semibold">
                        {format.label}
                      </span>
                      {selectedFormat === format.value && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500 }}
                        >
                          <CheckCircle2 size={20} className="text-[#D4AF37]" />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Investment Amount */}
            <div className="mb-8">
              <label className="block text-[#D4AF37] text-sm font-bold uppercase tracking-wider mb-4">
                {content.investment_label}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  step="10000"
                  min="100000"
                  max="500000"
                  className="w-full bg-white/5 border-2 border-white/10 rounded-xl px-6 py-4 text-white text-xl font-bold focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-white/50 text-xl font-bold">
                  €
                </span>
              </div>
            </div>

            {/* Monthly Profit */}
            <div>
              <label className="block text-[#D4AF37] text-sm font-bold uppercase tracking-wider mb-4">
                {content.profit_label}
              </label>
              <div className="relative">
                <input
                  type="range"
                  value={monthlyProfit}
                  onChange={(e) => setMonthlyProfit(Number(e.target.value))}
                  min="4000"
                  max="20000"
                  step="500"
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#D4AF37] mb-3"
                  style={{
                    background: `linear-gradient(to right, #D4AF37 0%, #D4AF37 ${((monthlyProfit - 4000) / 16000) * 100}%, rgba(255,255,255,0.1) ${((monthlyProfit - 4000) / 16000) * 100}%, rgba(255,255,255,0.1) 100%)`,
                  }}
                />
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">4 000 €</span>
                  <span className="text-white text-2xl font-bold">
                    {formatNumber(monthlyProfit)} €
                  </span>
                  <span className="text-white/50 text-sm">20 000 €</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="glass-gold rounded-3xl p-8 flex flex-col justify-between"
            style={{
              boxShadow: '0 20px 60px rgba(212,175,55,0.3), inset 0 1px 0 rgba(255,215,0,0.2)',
            }}
          >
            <div>
              {/* Result Title */}
              <div className="flex items-center gap-3 mb-6">
                <Clock size={28} className="text-[#D4AF37]" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {content.result_title}
                </h3>
              </div>

              {/* Big Payback Number */}
              <motion.div
                key={animatedPayback}
                className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-[#D4AF37]/30 to-transparent border border-[#D4AF37]/30"
              >
                <p className="text-[#D4AF37] text-sm uppercase tracking-wider mb-2 font-bold">
                  {content.payback_label}
                </p>
                <div className="flex items-baseline gap-2">
                  <motion.span
                    key={animatedPayback}
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-5xl md:text-6xl font-black text-white"
                  >
                    {animatedPayback}
                  </motion.span>
                  <span className="text-2xl text-white/70 font-semibold">
                    {content.payback_unit}
                  </span>
                </div>
              </motion.div>

              {/* Monthly Profit Display */}
              <div className="mb-8 p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp size={20} className="text-[#22C55E]" />
                  <p className="text-white/70 text-sm uppercase tracking-wider font-bold">
                    {content.profit_per_month}
                  </p>
                </div>
                <p className="text-3xl font-black text-white">
                  {formatNumber(monthlyProfit)} €
                </p>
              </div>

              {/* Disclaimer */}
              <p className="text-white/50 text-sm italic mb-6">
                {content.disclaimer}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3">
              <motion.a
                href="tel:+34624586917"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="btn-call flex items-center justify-center gap-3 px-6 py-4 rounded-full text-base font-bold shadow-xl"
              >
                <Phone size={20} />
                {content.cta_primary}
              </motion.a>
              <motion.a
                href="https://t.me/SerjValencia"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="btn-telegram flex items-center justify-center gap-3 px-6 py-4 rounded-full text-base font-bold shadow-xl"
              >
                <Send size={20} />
                {content.cta_secondary}
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
